import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import {
  ADD_EXISTING_FOOTER_PX,
  APP_NAV_HEIGHT_PX,
  DOCUMENT_SIDEBAR_WIDTH_PX,
  FULLSCREEN_DIALOG_APPBAR_PX,
  HASH_BANNER_HEIGHT_VAR,
} from './constants';

/**
 * Viewport-height shell under the app NavBar for split library/course pages.
 * The document root fills this shell and is the single scrollport.
 */
export const DocumentViewportShell = styled('div')({
  height: `calc(100vh - ${APP_NAV_HEIGHT_PX}px)`,
  width: '100%',
  maxWidth: '100%',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  boxSizing: 'border-box',
  '@media print': {
    height: 'auto',
    overflow: 'visible',
    display: 'block',
  },
});

/** List + document FlexibleLayout under the app NavBar. */
export const DocumentSplitShell = DocumentViewportShell;

/**
 * Full-width document only (hashed share; no sidebar, NavBar hidden).
 * Height tracks HashAccessBanner via --hash-banner-height (ResizeObserver) so
 * wrapped banners on small screens do not clip the document panel.
 */
export const DocumentSoloShell = styled('div')({
  height: `calc(100vh - var(${HASH_BANNER_HEIGHT_VAR}, 0px))`,
  width: '100%',
  maxWidth: '100%',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  boxSizing: 'border-box',
  '@media print': {
    height: 'auto',
    overflow: 'visible',
    display: 'block',
  },
});

/** List + document split inside Add Existing fullscreen (app bar + bottom bar). */
export const AddExistingSplitShell = styled('div')({
  height: `calc(100vh - ${FULLSCREEN_DIALOG_APPBAR_PX}px - ${ADD_EXISTING_FOOTER_PX}px)`,
  width: '100%',
  overflow: 'hidden',
});

export const Container = styled(Box)({
  height: '100%',
  width: '100%',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  '@media print': {
    height: 'auto',
    overflow: 'visible',
    display: 'block',
  },
});

export const MobileSlider = styled(Box)(({ theme }) => ({
  display: 'flex',
  height: '100%',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.standard,
  }),
  // Do not set transform until the right panel is shown. A transform on this
  // 200vw track makes position:fixed descendants (document-list Print # /
  // Print All) position against the slider instead of the viewport, which
  // places them in the off-screen right half on mobile.
  [theme.breakpoints.down('lg')]: {
    width: '200vw',
    '&.showRight': {
      transform: 'translateX(-100vw)',
    },
  },
  [theme.breakpoints.up('lg')]: {
    width: '100%',
  },
  '@media print': {
    display: 'block',
    width: '100% !important',
    height: 'auto',
    transform: 'none !important',
  },
}));

export const WebLayout = styled(Box)(({ theme }) => ({
  height: '100%',
  overflow: 'hidden',
  [theme.breakpoints.down('lg')]: {
    position: 'relative',
  },
  [theme.breakpoints.up('lg')]: {
    display: 'flex',
    justifyContent: 'center',
  },
  '@media print': {
    height: 'auto',
    overflow: 'visible',
    display: 'block',
  },
}));

export const WebLeftPanel = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'variant',
})(({ theme, variant }) => ({
  height: '100%',
  // Library sidebar: overflow lives on DocumentList so scrollTop survives
  // mobile slide-away / slide-back. SubScore (default) still scrolls here.
  overflow: variant === 'sidebar' ? 'hidden' : 'auto',
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  minWidth: 0,
  // Width animation is desktop-only; mobile slides the unchanged full-width panel.
  [theme.breakpoints.up('lg')]: {
    transition: theme.transitions.create('width', {
      duration: theme.transitions.duration.standard,
    }),
  },

  '&.split': {
    width: variant === 'sidebar' ? DOCUMENT_SIDEBAR_WIDTH_PX : '40%',
    flexShrink: 0,
    ...(variant === 'sidebar'
      ? {
          '@media print': {
            display: 'none !important',
          },
        }
      : null),
  },
  [theme.breakpoints.down('lg')]: {
    // Keep a full viewport width while off-screen so the list doesn't reflow/jumble.
    width: '100vw',
    flexShrink: 0,
    '&.split': {
      width: '100vw',
    },
  },
}));

export const WebRightPanel = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'variant',
})(({ theme, variant }) => ({
  height: '100%',
  // Sidebar docs: Document/CourseDocument own scrolling. Default (SubScore) still
  // scrolls the panel itself.
  overflow: variant === 'sidebar' ? 'hidden' : 'auto',
  display: 'flex',
  flexDirection: 'column',
  transition: theme.transitions.create(['width', 'opacity'], {
    duration: theme.transitions.duration.standard,
  }),
  width: '0%',
  opacity: 0,
  minWidth: 0,

  '&.visible': {
    width: variant === 'sidebar' ? `calc(100% - ${DOCUMENT_SIDEBAR_WIDTH_PX}px)` : '60%',
    flex: variant === 'sidebar' ? 1 : undefined,
    opacity: 1,
    ...(variant === 'sidebar'
      ? {
          '@media print': {
            display: 'block',
            width: '100% !important',
            height: 'auto',
            overflow: 'visible',
            flex: 'none',
            opacity: '1 !important',
          },
        }
      : null),
    [theme.breakpoints.down('lg')]: {
      width: '100vw',
      flexShrink: 0,
    },
  },
  [theme.breakpoints.down('lg')]: {
    // Avoid width tween on mobile — the slider transform owns the motion.
    transition: theme.transitions.create('opacity', {
      duration: theme.transitions.duration.standard,
    }),
  },
}));

export const PanelContent = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'fullWidth',
})(({ theme, fullWidth }) => ({
  height: '100%',
  width: '100%',
  maxWidth: fullWidth ? 'none' : theme.breakpoints.values.md,
  display: 'flex',
  flexDirection: 'column',
  // fullWidth (library): DocumentList owns scrolling so position is preserved.
  overflow: fullWidth ? 'hidden' : 'auto',
  margin: fullWidth ? 0 : '0 auto',
  minHeight: 0,
  '@media print': {
    height: 'auto',
    maxWidth: 'none',
    overflow: 'visible',
    display: 'block',
  },
}));

// Legacy styles kept for backward-compatibility (referenced by external
// consumers, even though FlexibleLayout itself no longer uses them).
export const LeftPanel = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  overflow: 'hidden',
  minHeight: 0,
});

export const RightPanel = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  overflow: 'hidden',
  minHeight: 0,
  width: '100%',
});

export const PanelGroup = styled(Box)({
  height: '100%',
  width: '100%',
});

export const RightPanelShown = styled(Box)({
  transform: 'translateX(0)',
  opacity: 1,
  pointerEvents: 'auto',
  transition: 'transform 0.5s ease, opacity 0.5s ease',
  height: '100%',
  width: '100%',
});

export const RightPanelHidden = styled(Box)({
  transform: 'translateX(100%)',
  opacity: 0,
  pointerEvents: 'none',
  transition: 'transform 0.5s ease, opacity 0.5s ease',
  height: '100%',
  width: '100%',
});

export const ResizeHandle = styled(Box)(({ theme }) => ({
  width: '2px',
  backgroundColor: 'transparent',
  alignItems: 'center',
  justifyItems: 'center',
  position: 'relative',
  cursor: 'col-resize',

  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: '-2px',
    right: '-2px',
    backgroundColor: theme.palette.action.hover,
    transition: 'background-color 0.2s',
  },
  '&:hover::before': {
    backgroundColor: theme.palette.action.selected,
  },
}));
